'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import React, { useCallback, useMemo } from 'react';
import { StepIndicator } from './step-indicator';
import { trackEvent } from '@/lib/event-tracking';
import { useAIPlannerStore, type AgeRange } from '@/store/ai-planner';

type Gender = 'male' | 'female';

const AGE_RANGE_OPTIONS: { value: AgeRange; label: string }[] = [
  { value: 'under_18', label: 'Dưới 18' },
  { value: '18_22', label: '18 – 22' },
  { value: '23_30', label: '23 – 30' },
  { value: '31_40', label: '31 – 40' },
  { value: 'over_40', label: 'Trên 40' },
];

const GENDER_OPTIONS: { value: Gender; label: string; avatar: string }[] = [
  {
    value: 'male',
    label: 'Nam',
    avatar: `${assetPrefix}/static/images/avatar-male.webp`,
  },
  {
    value: 'female',
    label: 'Nữ',
    avatar: `${assetPrefix}/static/images/avatar-female.webp`,
  },
];

export function StepPersonalInfo() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const nextStep = useAIPlannerStore.use.nextStep();
  const prevStep = useAIPlannerStore.use.prevStep();

  const handleGenderSelect = useCallback(
    (gender: Gender) => {
      setPreferences({ gender });
    },
    [setPreferences]
  );

  const handleAgeRangeSelect = useCallback(
    (ageRange: AgeRange) => {
      setPreferences({ ageRange });
    },
    [setPreferences]
  );

  const handleBudgetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
      const numValue = value ? parseInt(value, 10) : undefined;
      setPreferences({ budget: numValue });
    },
    [setPreferences]
  );

  const formatBudget = useMemo(() => {
    if (!preferences.budget) return '';
    return new Intl.NumberFormat('vi-VN').format(preferences.budget);
  }, [preferences.budget]);

  return (
    <div className="p-0">
      {/* Background Image */}
      <div className="absolute -top-40 inset-x-0 w-full h-auto flex items-start justify-center py-4 aspect-square">
        <Image
          src={`${assetPrefix}/static/icons/ai-planner-page-2-bg.svg`}
          alt="planner personal info background"
          fill
          className="object-contain"
          priority
        />
      </div>

      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-88px-24px)] overflow-auto">
        {/* Banner */}
        <div className="relative mb-4 flex items-center justify-center pt-4 h-28">
          {/* Mascot bên trái */}
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 z-20">
            <Image
              src={`${assetPrefix}/static/images/ai-planner-tab-icon.webp`}
              alt="AI Planner Banner"
              width={140}
              height={140}
              className="object-contain scale-[1.6]"
            />
          </div>

          {/* Banner */}
          <div
            className="
              flex items-start gap-1
              h-fit
              ml-4
              mb-6
              pl-24 pr-8
              rounded-full
              bg-gradient-to-r from-[#FF7A45] to-[#FF6A3D]
            "
          >
            {/* icon spark */}
            <Image
              width={28}
              height={28}
              src={`${assetPrefix}/static/icons/white-ai.svg`}
              alt=""
              className="mr-3 object-contain"
            />

            {/* text */}
            <span className="text-white text-xs md:text-base font-semibold py-2 leading-none">
              Cho Amori biết thêm thông tin cá nhân và sở thích ăn uống để tìm
              món đúng gu bạn nhất nhé!
            </span>
          </div>
        </div>

        {/* Age Range Selection */}
        <div className="mb-4">
          <p className="text-white text-sm font-semibold flex mb-2">Độ tuổi</p>
          <div className="grid grid-cols-3 gap-2 items-center justify-center">
            {AGE_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAgeRangeSelect(option.value)}
                className={cn(
                  'px-4 py-2.5 rounded-full text-sm transition-all text-white font-semibold',
                  preferences.ageRange === option.value
                    ? 'ai-planner-button-active'
                    : 'ai-planner-button'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Input */}
        <div className="mb-4">
          <p className="text-white text-sm font-semibold flex mb-2">
            Ngân sách mỗi bữa (tùy chọn)
          </p>
          <div className="relative">
            <input
              type="text"
              value={formatBudget}
              onChange={handleBudgetChange}
              placeholder="Nhập ngân sách cho mỗi bữa"
              className="w-full px-4 py-3 bg-white/5 ai-planner-input rounded-xl text-white placeholder:text-white/40 focus:bg-white/10 focus:outline-none transition-colors h-auto border-none text-base"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 text-base">
              ₫
            </span>
          </div>
          <p className="text-white/40 text-xs mt-2">
            Ví dụ: 50.000đ, 100.000đ, 150.000đ
          </p>
        </div>

        {/* Gender Selection */}
        <div className="mb-6">
          <p className="text-white text-sm font-semibold mb-3">Giới tính</p>
          <div className="flex justify-center gap-4">
            {GENDER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleGenderSelect(option.value)}
                className={cn(
                  'flex flex-col items-center gap-3 px-8 py-4 rounded-2xl transition-all relative',
                  preferences.gender === option.value
                    ? 'ai-planner-button-active'
                    : 'ai-planner-button'
                )}
              >
                <div className="relative size-20 rounded-full overflow-hidden">
                  <Image
                    src={option.avatar}
                    alt={option.label}
                    fill
                    className="object-cover"
                  />
                </div>
                {preferences.gender === option.value && (
                  <div className="absolute top-1 right-1 bg-primary-40 rounded-full p-1">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3334 4L6.00002 11.3333L2.66669 8"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
                <span className="text-base font-semibold text-white">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Info Note */}
        <p className="text-white/40 text-xs text-center mb-6">
          Bạn có thể chọn "Tiếp tục" nếu không có thái gian và cập
          <br />
          nhật sau toi trong Ha của cá nhân.
        </p>

        {/* Spacer for bottom button */}
        <div className="h-24" />
      </ScrollArea>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className="glass-bubble rounded-full flex justify-between items-center px-4 py-2 h-16">
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                button_name: 'meal_plan_back',
                button_location: currentStep,
              });
              prevStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            ← Quay lại
          </Button>
          <StepIndicator />
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                event_name: 'click_button',
                button_name: 'meal_plan_step',
                button_location: currentStep,
              });
              nextStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            Tiếp tục →
          </Button>
        </div>
      </div>
    </div>
  );
}
